/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { func, string, shape, array } from 'prop-types';
import { connect } from 'react-redux';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import isEqual from 'lodash/isEqual';

import p from 'polyglot/polyglot';

import { makeUserPictures } from 'selectors/userPictures';
import { updatePicturesOrder as updatePicturesOrderAction } from 'actions/user';

import ImageModal from 'components/ImageModal/ImageModal';

import AddPicture from './AddPicture/AddPicture';
import Picture from './Picture/Picture';
import DragDropCard from './DragDropCard/DragDropCard';

import styles from './portfolio.scss';

/**
 * This is the Portfolio component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class Portfolio extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {Object}
   */
  static propTypes = {
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    user: shape({
      type: string.isRequired,
    }).isRequired,
    pictures: shape({
      polaroids: array.isRequired,
      portfolio: array.isRequired,
    }).isRequired,
    updatePicturesOrder: func.isRequired,
  };

  /**
   * Create Portfolio.
   *
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    const {
      pictures: { portfolio },
    } = this.props;

    // We use state to move without sending to the server,
    // and then we send on drop instead.
    this.state = {
      portfolioImages: portfolio,
      curentModalIndex: null,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      pictures: { portfolio: prevPortfolio },
    } = prevProps;
    const {
      pictures: { portfolio },
    } = this.props;

    // if images change, we update the cached images.
    if (!isEqual(prevPortfolio, portfolio)) {
      this.setState({
        portfolioImages: portfolio,
      });
    }
  }

  onMoveCard = (dragIndex, hoverIndex) => {
    const { portfolioImages: cards } = this.state;
    const dragCard = cards[dragIndex];

    this.setState({
      portfolioImages: update(cards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragCard],
        ],
      }),
    });
  };

  onDropCard = () => {
    const { portfolioImages } = this.state;
    const {
      auth: { token },
      updatePicturesOrder,
    } = this.props;
    const pictureIds = [];

    portfolioImages.map(picture => {
      if (typeof picture !== 'undefined') {
        pictureIds.push(picture.picture_id);
      }
    });

    updatePicturesOrder(token, {
      picture_ids: pictureIds,
    });
  };

  onClickPicture = index => this.setState({ curentModalIndex: index });

  onCloseModal = () => this.setState({ curentModalIndex: null });

  /**
   * Render Portfolio.
   *
   * @return {Object}
   */
  render() {
    const {
      user,
      pictures: { polaroids, portfolio },
    } = this.props;

    const { portfolioImages, curentModalIndex } = this.state;

    const nextEmptyPortfolioIndex = portfolio
      .filter((p, i) => i > 2)
      .indexOf(undefined);

    const nextPortfolioIndex =
      nextEmptyPortfolioIndex === -1 ? 3 : nextEmptyPortfolioIndex + 3;

    const lightbox = [...polaroids, ...portfolio];

    // Filter empty images from array.
    const lightboxArray = lightbox.filter(image => !!image);

    return (
      <article className={styles.wrapper}>
        {user.roles.default_role === 'MODEL' && (
          <section>
            <header className={styles.header}>
              <h2>{p.t('portfolio.title.polaroid')}</h2>
            </header>
            <ul className={styles.pictureList}>
              {polaroids.map((picture, i) => {
                // No picture at this index, let the user upload.
                if (typeof picture === 'undefined') {
                  return (
                    <li key={i}>
                      <h4 className={styles.label}>
                        {p.t(`portfolio.labels.polaroid.index_${i}`)}
                      </h4>
                      <div className={styles.listItem}>
                        <AddPicture
                          id={`polaroidInput-${i}`}
                          preview={p.t(
                            `portfolio.previews.polaroid.index_${i}`
                          )}
                          index={i}
                        />
                      </div>
                    </li>
                  );
                }

                // Render picture.
                return (
                  <li key={picture.picture_id}>
                    <h4 className={styles.label}>
                      {p.t(`portfolio.labels.polaroid.index_${i}`)}
                    </h4>
                    <div className={styles.listItem}>
                      <Picture {...picture} onClick={this.onClickPicture} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section>
          <header className={styles.header}>
            <h2>{p.t('portfolio.title.portfolio')}</h2>
          </header>
          <ul className={styles.pictureList}>
            {portfolioImages.map((picture, i) =>
              typeof picture !== 'undefined' ? (
                <li key={picture.picture_id}>
                  <div className={styles.listItem}>
                    <DragDropCard
                      id={picture.picture_id}
                      index={i}
                      onMove={this.onMoveCard}
                      onDrop={this.onDropCard}
                    >
                      <Picture {...picture} onClick={this.onClickPicture} />
                    </DragDropCard>
                  </div>
                </li>
              ) : null
            )}
            <li>
              <div className={styles.listItem}>
                <AddPicture id="portfolioInput" index={nextPortfolioIndex} />
              </div>
            </li>
          </ul>
        </section>

        {curentModalIndex !== null && (
          <ImageModal
            images={lightboxArray}
            index={curentModalIndex}
            onClose={this.onCloseModal}
          />
        )}
      </article>
    );
  }
}

/**
 * Map state from redux store to props.
 */
const makeMapStateToProps = () => {
  const userPictures = makeUserPictures();

  const mapStateToProps = (state, props) => {
    return {
      auth: state.auth,
      user: state.user,
      pictures: userPictures(state, props),
    };
  };
  return mapStateToProps;
};

export default connect(makeMapStateToProps, {
  updatePicturesOrder: updatePicturesOrderAction,
})(DragDropContext(HTML5Backend)(Portfolio));
